import React, { useState, useEffect, useMemo } from 'react';
import { createPageUrl } from "@/utils";
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  ArrowLeft,
  ChevronsUpDown,
  Check,
  FileText,
} from 'lucide-react';
import { runEndpoint } from '@/services/flow';
import { fetchData } from '@/fetchapi/fetchapi';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import RecordsScoreTable from '@/components/act-scores/RecordsScoreTable';

export default function ACTStatistics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dataSource, setDataSource] = useState(null);
  const [dateEntries, setDateEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedDatePath, setSelectedDatePath] = useState(searchParams.get('datePath') || null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (id) {
      loadDataSource();
      loadFiles();
    }
  }, [id]);

  useEffect(() => {
    if (selectedDatePath) {
      setSearchParams({ datePath: selectedDatePath });
    }
  }, [selectedDatePath]);

  const loadDataSource = async () => {
    try {
      const data = await fetchData(`data_source?id=${id}`);
      setDataSource(data);
    } catch (error) {
      console.error('Error loading data source:', error);
      toast.error('Failed to load data source');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFiles = async () => {
    setFilesLoading(true);
    try {
      const response = await runEndpoint('cortex.vdd.list_datasource_files', {
        dataSourceId: id
      });

      // Handle new response shape: { files: [{ datePath, files, fileCount, totalSize, latestModified }] }
      const rawEntries = response?.files || response || [];
      setDateEntries(rawEntries);

      // Auto-select from URL or first entry
      if (!selectedDatePath && rawEntries.length > 0) {
        const urlDatePath = searchParams.get('datePath');
        if (urlDatePath) {
          setSelectedDatePath(urlDatePath);
        } else {
          setSelectedDatePath(rawEntries[0].datePath);
        }
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const filteredEntries = useMemo(() => {
    if (!search) return dateEntries;
    const query = search.toLowerCase();
    return dateEntries.filter(entry =>
      entry.datePath?.toLowerCase().includes(query)
    );
  }, [dateEntries, search]);

  const selectedEntry = dateEntries.find(e => e.datePath === selectedDatePath);

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(createPageUrl(`data-sources/${id}`))}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Data Source
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-slate-900">
            ACT Statistics
          </h1>
          {dataSource && (
            <p className="text-slate-600 mt-1">{dataSource.source_name}</p>
          )}
        </div>
      </div>

      {/* File Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Select File
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filesLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : dateEntries.length === 0 ? (
            <p className="text-slate-500">No files available</p>
          ) : (
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between max-w-xl"
                >
                  <span className="truncate">
                    {selectedEntry
                      ? `${selectedEntry.datePath} (${selectedEntry.fileCount || selectedEntry.files?.length || 0} files)`
                      : 'Select date...'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[500px] p-0">
                <Command>
                  <CommandInput
                    placeholder="Search dates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <CommandList>
                    {filteredEntries.length === 0 ? (
                      <CommandEmpty>No dates found.</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {filteredEntries.map((entry) => (
                          <CommandItem
                            key={entry.datePath}
                            onClick={() => {
                              setSelectedDatePath(entry.datePath);
                              setComboboxOpen(false);
                              setSearch('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedDatePath === entry.datePath ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex-1">
                              <div className="font-medium">{entry.datePath}</div>
                              <div className="text-xs text-slate-500">
                                {entry.fileCount || entry.files?.length || 0} files
                                {entry.latestModified ? ` · ${new Date(entry.latestModified).toLocaleDateString()}` : ''}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </CardContent>
      </Card>

      {/* Records Table */}
      {selectedDatePath && (
        <RecordsScoreTable
          dataSourceId={id}
          datePath={selectedDatePath}
        />
      )}
    </div>
  );
}
